import React, { useState, useEffect } from 'react';
import { parseLocalDate } from '../lib/dateFmt';
import { Calendar, MapPin, ExternalLink, Clock, CalendarDays, Check, UserPlus } from 'lucide-react';
import { api } from '../lib/api';
import { normalizeHtml } from '../lib/htmlText';
import PageHero from '../components/PageHero';
import PageBlocks from '../components/PageBlocks';
import RichContentBlocks from '../components/RichContentBlocks';
import { useTranslation } from '../i18nContext';
import { useAuth } from '../lib/AuthContext';

export default function EventsPage() {
  const t = useTranslation();
  const { user } = useAuth();
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myRegistrations, setMyRegistrations] = useState(new Set());  // event_ids the user is registered for
  const [busy, setBusy] = useState('');
  const dateLocale = t.locale === 'en' ? 'en-US' : 'es-MX';

  useEffect(() => {
    api.get('/events').then(({ data }) => {
      const rows = data || [];
      setUpcoming(rows.filter((e) => !e.is_past));
      setPast(rows.filter((e) => e.is_past));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user) { setMyRegistrations(new Set()); return; }
    api.get('/me/event-history').then(({ data }) => {
      setMyRegistrations(new Set((data || []).map((r) => r.event_id)));
    }).catch(() => {});
  }, [user]);

  async function toggleRegister(eventId) {
    if (!user) return;
    setBusy(eventId);
    try {
      if (myRegistrations.has(eventId)) {
        await api.delete(`/events/${eventId}/register`);
        setMyRegistrations((prev) => { const n = new Set(prev); n.delete(eventId); return n; });
      } else {
        await api.post(`/events/${eventId}/register`);
        setMyRegistrations((prev) => new Set([...prev, eventId]));
      }
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Error');
    } finally { setBusy(''); }
  }

  const formatDate = (s) => parseLocalDate(s).toLocaleDateString(dateLocale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const formatTime = (s) => new Date(s).toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit' });
  const formatShortDate = (s) => parseLocalDate(s).toLocaleDateString(dateLocale, { month: 'short' });

  return (
    <>
      <PageHero label={t.events.label} title={t.events.title} subtitle={t.events.subtitle} />
      <PageBlocks pageKey="events" />
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-16">
              <div className="w-10 h-10 border-3 border-cmic-200 border-t-cmic-500 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-charcoal-500">{t.events.loading}</p>
            </div>
          ) : (
            <>
              {upcoming.length > 0 ? (
                <div className="space-y-4 mb-20">
                  <h2 className="text-2xl font-bold text-charcoal-800 mb-6">{t.events.scheduled}</h2>
                  {upcoming.map((event) => (
                    <div key={event.id} data-testid={`event-upcoming-${event.id}`} className="bg-gray-50 rounded-xl p-6 sm:p-8 hover:shadow-md transition-all duration-300 border border-gray-100 hover:border-cmic-200 flex flex-col sm:flex-row gap-6">
                      <div className="flex-shrink-0 w-20 h-20 bg-cmic-500 rounded-xl flex flex-col items-center justify-center text-white">
                        <span className="text-2xl font-bold leading-none">{new Date(event.event_date).getDate()}</span>
                        <span className="text-xs uppercase mt-1 font-medium">{formatShortDate(event.event_date)}</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-charcoal-800 mb-2">{event.title}</h3>
                        <div className="text-charcoal-500 mb-4 leading-relaxed prose prose-sm max-w-none prose-headings:text-charcoal-700 prose-a:text-cmic-600 prose-strong:text-charcoal-700" dangerouslySetInnerHTML={{ __html: normalizeHtml(event.description || '') }} />
                        <RichContentBlocks blocks={event.content_blocks} />
                        <div className="flex flex-wrap gap-4 text-sm text-charcoal-500">
                          <span className="flex items-center gap-1.5"><Calendar size={14} className="text-cmic-500" />{formatDate(event.event_date)}{event.event_end_date && event.event_end_date !== event.event_date && <> – {formatDate(event.event_end_date)}</>}</span>
                          <span className="flex items-center gap-1.5"><Clock size={14} className="text-cmic-500" />{formatTime(event.event_date)}</span>
                          {event.location && (<span className="flex items-center gap-1.5"><MapPin size={14} className="text-cmic-500" />{event.location}</span>)}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-4">
                          {event.allow_registration && user && (
                            <button
                              onClick={() => toggleRegister(event.id)}
                              disabled={busy === event.id}
                              data-testid={`event-register-btn-${event.id}`}
                              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 ${myRegistrations.has(event.id) ? 'bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-emerald-50' : 'bg-cmic-600 text-white hover:bg-cmic-700'}`}
                            >
                              {myRegistrations.has(event.id) ? (<><Check size={14} /> Estás registrado · cancelar</>) : (<><UserPlus size={14} /> Registrarme</>)}
                            </button>
                          )}
                          {event.allow_registration && !user && (
                            <span className="text-xs text-charcoal-500 italic">Inicia sesión para registrarte a este evento.</span>
                          )}
                          {event.registration_url && (
                            <a href={event.registration_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-cmic-500 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-cmic-600 transition-colors">
                              {t.events.register}<ExternalLink size={14} />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-gray-50 rounded-2xl border border-gray-100 mb-20">
                  <CalendarDays size={48} className="text-charcoal-300 mx-auto mb-4" />
                  <p className="text-charcoal-500 text-lg mb-2">{t.events.emptyTitle}</p>
                  <p className="text-charcoal-400 text-sm">{t.events.emptySubtitle}</p>
                </div>
              )}

              {past.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-charcoal-800 mb-6">{t.events.past}</h2>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {past.map((event) => (
                      <div key={event.id} className="bg-gray-50 rounded-xl overflow-hidden border border-gray-100">
                        {event.image_url && (
                          <div className="aspect-video overflow-hidden">
                            <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
                          </div>
                        )}
                        <div className="p-5">
                          <p className="text-xs text-charcoal-400 mb-1">{formatDate(event.event_date)}</p>
                          <h4 className="font-medium text-charcoal-700 mb-1">{event.title}</h4>
                          {event.description && (
                            <div className="text-sm text-charcoal-500 line-clamp-3 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: normalizeHtml(event.description) }} />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
}
